import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom'

import AppShell from './components/shell/AppShell'
import { landingPath } from './components/shell/nav'
import { identifyUser, trackPageView } from './lib/analytics'
import { API_BASE, ApiError, ORG_SWITCHED_EVENT, getSession, setShownOrg, type Session } from './lib/api'
import { ensurePreconnect } from './lib/preconnect'
import { CLUB_PARAM, switchToClub } from './lib/orgAlign'
import { SessionProvider, useSession } from './lib/session'
import { pathSeg } from './lib/urls'
import Activity from './pages/Activity'
import Dashboard from './pages/Dashboard'
import DeveloperTools from './pages/DeveloperTools'
import ManageApi from './pages/ManageApi'
import Games from './pages/Games'
import Glossary from './pages/Glossary'
import Insights from './pages/Insights'
import ReportViewer from './pages/ReportViewer'
import ManageInsights from './pages/ManageInsights'
import InsightsGame from './pages/InsightsGame'
import InsightPackViewer from './pages/InsightPackViewer'
import AnalyticsCollaboration from './pages/AnalyticsCollaboration'
import InsightsWorkshop from './workshop/InsightsWorkshop'
import ManageClubs from './pages/ManageClubs'
import CalibrationQueue from './pages/CalibrationQueue'
import CalibrationReview from './pages/CalibrationReview'
import TrackingCompare from './pages/qa/TrackingCompare'
import FormationsLab from './pages/qa/FormationsLab'
import RegainsLab from './pages/qa/RegainsLab'
import LabelingIndex from './pages/labeling/LabelingIndex'
import BallGrid from './pages/labeling/BallGrid'
import JerseyLegibility from './pages/labeling/JerseyLegibility'
import JerseyNumbers from './pages/labeling/JerseyNumbers'
import SequenceLabeler from './pages/labeling/SequenceLabeler'
import LeadLinks from './pages/LeadLinks'
import LeadUploadPortal from './pages/upload/LeadUploadPortal'
import NotebookDetail from './pages/notebooks/NotebookDetail'
import PerformanceSummary from './pages/perf/PerformanceSummary'
import Login from './pages/auth/Login'
import Verify2FA from './pages/auth/Verify2FA'
import ForgotPassword from './pages/auth/ForgotPassword'
import ResetPassword from './pages/auth/ResetPassword'
import RequestAccess from './pages/auth/RequestAccess'
import AcceptTerms from './pages/auth/AcceptTerms'
import Terms from './pages/auth/Terms'
import Privacy from './pages/auth/Privacy'
import ManageUsers from './pages/ManageUsers'
import Operations from './pages/Operations'
import LayerStackOps from './ops/LayerStack'
import VideoTimes from './ops/VideoTimes'
import MatchLab from './ops/MatchLab'
import VideoQA from './ops/VideoQA'
import ClipRoom from './pages/ClipRoom'
import PlaylistEditor from './pages/playlists/PlaylistEditor'
import PlaylistPlayer from './pages/playlists/PlaylistPlayer'
import QaStudioV2 from './pages/QaStudioV2'
import Status from './pages/Status'
import UploadMatch from './pages/UploadMatch'

// Lazy, unlike every other page here. The match page pulls in the whole canvas
// stack — telestration, tracking, formations, heat maps — and it is the one
// screen most people never open. Loading it eagerly made the bundle everyone
// downloads on sign-in carry it too.
const GameMatchSummary = lazy(() =>
  import('./pages/match/GameMatchSummary').then((m) => ({ default: m.GameMatchSummary })),
)
// The bare viewer is small and needs none of that, so it is its own chunk.
const MatchVideoOnly = lazy(() => import('./pages/match/MatchVideoOnly'))
const DataDrivenClipping = lazy(() => import('./pages/DataDrivenClipping'))

type State =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'needs-tos' }
  | { status: 'ready'; session: Session }
  | { status: 'error'; message: string }

/** Sign-in is now a React page at /login; ?next brings the person back. */
function signInUrl(): string {
  return `/login?next=${encodeURIComponent(window.location.href)}`
}

/** The match page at /match-room/<code> — or, with ?view=video, the bare
 *  viewer the Insights Lab and Workshop open (just the video and its
 *  playback controls). Mounted by the canonical route; /match-video and
 *  /tactical-lab redirect here. */
function MatchVideoRoute() {
  const [params] = useSearchParams()
  return (
    <ClubGate>
      {params.get('view') === 'video' ? <MatchVideoOnly /> : <GameMatchSummary />}
    </ClubGate>
  )
}

/** ?club=<id> on a match link: open that club's copy of the game. The page's
 *  APIs answer for the session's CURRENT club, so a different club is
 *  switched to first — through the API, on this app's own session — and the
 *  page reloads without the parameter, so a reload never switches again and
 *  nothing loads in the wrong club meanwhile. A switch the server refuses
 *  falls through to the page, whose own not-in-club handling speaks. (Layers'
 *  Match Video linked the legacy Flask route for this, which sent anyone it
 *  could not authenticate to the old Jinja login — Ben, 2026-10-27.) */
function ClubGate({ children }: { children: React.ReactNode }) {
  const session = useSession()
  const [params] = useSearchParams()
  const raw = params.get(CLUB_PARAM)
  const club = raw && /^\d+$/.test(raw) ? Number(raw) : null
  const needed = club !== null && session.org?.id !== club
  const [refused, setRefused] = useState(false)
  useEffect(() => {
    if (!needed) return
    let cancelled = false
    switchToClub(club!).then((ok) => {
      if (cancelled) return
      if (!ok) { setRefused(true); return }
      const url = new URL(window.location.href)
      url.searchParams.delete(CLUB_PARAM)
      window.location.replace(url.toString())
    })
    return () => { cancelled = true }
  }, [needed, club])
  if (needed && !refused) return <p className="muted" style={{ padding: 24 }}>Opening the match in its club…</p>
  return <>{children}</>
}

/** Every old match-page address -> /match-room/<code>, keeping the code AND
 *  the query. /tactical-lab and /match-video each had their day as the name
 *  (Match Room since 2026-10-23, Ben). Deep links carry ?from=, ?filter=,
 *  ?frame= and the page reads all of them; a redirect that drops them still
 *  loads, just not on what was clicked. */
function LegacyMatchRedirect() {
  const { gameCode = '' } = useParams<{ gameCode: string }>()
  return <Navigate to={`/match-room/${pathSeg(gameCode)}${window.location.search}`} replace />
}

/** /clip-lab/<code> -> /clip-room/<code>, query preserved — same contract as
 *  LegacyMatchRedirect, for the Clip Room's old address. */
function LegacyClipRedirect() {
  const { gameCode = '' } = useParams<{ gameCode: string }>()
  return <Navigate to={`/clip-room/${pathSeg(gameCode)}${window.location.search}`} replace />
}

/** The Jinja calibration fallback at /orig was retired (Phase 1 of the
 *  port); a bookmarked /orig URL — query string included, real shapes
 *  exist — lands on the React page for the same job. */
function CalibOrigRedirect() {
  const { jobId = '' } = useParams<{ jobId: string }>()
  return <Navigate to={`/games/${jobId}/calibration${window.location.search}`} replace />
}

/** Phase-1 flips: the retired Jinja pages' bookmarks land on their React
 *  successors, query string preserved. */
function OpsRedirect() {
  return <Navigate to={`/pipeline${window.location.search}`} replace />
}

function FixturesRedirect() {
  return <Navigate to={`/match-room${window.location.search}`} replace />
}

/** The management pages moved from /insights-lab to /manage-insights
 *  (2026-09-07) when the Insights Lab took the old address. Game and report
 *  deep links keep their whole tail and query, so a bookmarked report opens
 *  the same report at the new address. */
function LabToManageRedirect() {
  const { pathname, search } = useLocation()
  return <Navigate replace
    to={pathname.replace(/^\/insights-lab/, '/manage-insights') + search} />
}

/** /insights-workshop[/<tab>] -> /analysis-room[/<tab>], tail and query
 *  kept (Analysis Room since Ben, 2026-10-21). Bookmarks, shared tab links
 *  and the UAT route list all carry the old address. */
function LegacyWorkshopRedirect() {
  const { pathname, search } = useLocation()
  return <Navigate replace
    to={pathname.replace(/^\/insights-workshop/, '/analysis-room') + search} />
}

/** Both landing destinations are React routes now — /status for staff,
 *  /dashboard for everyone else — so this is a router navigation rather than
 *  the full-page replace it used to be while /home was still Jinja. */
function Landing({ session }: { session: Session }) {
  return <Navigate to={landingPath(session)} replace />
}

function NotFound() {
  return (
    <>
      <h1 className="page-title">Not found</h1>
      <p className="page-sub">
        Nothing serves <code>{window.location.pathname}</code>. If a link
        brought you here, the link is wrong — please say so.
      </p>
    </>
  )
}

/** Routes that render WITHOUT a session — before sign-in, or public legal
 *  pages. Checked FIRST: if the pathname matches, the component renders and
 *  the session fetch never fires. Everything else falls through to the
 *  session-gated main app. */
const PRE_AUTH_ROUTES: Record<string, () => React.JSX.Element> = {
  '/login': () => <Login />,
  '/auth/login': () => <Login />,
  '/forgot-password': () => <ForgotPassword />,
  '/auth/forgot-password': () => <ForgotPassword />,
  '/reset-password': () => <ResetPassword />,
  '/request-access': () => <RequestAccess />,
  '/auth/request-access': () => <RequestAccess />,
  '/accept-terms': () => <AcceptTerms />,
  '/terms-of-service': () => <Terms />,
  '/terms': () => <Terms />,
  '/privacy-policy': () => <Privacy />,
  '/privacy': () => <Privacy />,
  '/verify-2fa': () => <Verify2FA />,
}

/** The one pre-auth PREFIX: the public lead-upload portal lives at
 *  /upload/<slug>, and the slug is the capability — there is no session
 *  to gate on. An exact-pathname map cannot express it, so it is its own
 *  check rather than a widening of the map's contract. */
function preAuthPrefix(pathname: string): (() => React.JSX.Element) | null {
  if (/^\/upload\/[^/]+\/?$/.test(pathname)) return () => <LeadUploadPortal />
  return null
}

/** Page View on every in-app route change. The initial document load is
 *  main.tsx's pageview; this skips its first location so the two never
 *  double-count, and fires for every navigation after that. */
function PageViewTracker() {
  const location = useLocation()
  const first = useRef(true)
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    trackPageView()
  }, [location])
  return null
}

/** Wrapper that checks pre-auth routes before rendering the session-gated
 *  app. Hooks rules require this to be a separate component so that the
 *  main App's hooks are never called conditionally. */
export default function App() {
  const preAuth = PRE_AUTH_ROUTES[window.location.pathname]
    ?? preAuthPrefix(window.location.pathname)
  if (preAuth) return preAuth()
  return <AuthenticatedApp />
}

function AuthenticatedApp() {
  const [state, setState] = useState<State>({ status: 'loading' })

  useEffect(() => {
    getSession()
      .then((session) => {
        identifyUser(session)
        // Warm the media-origin connection now: by the time a match page
        // learns its presigned video URL, the TLS handshake is done.
        ensurePreconnect(session.media_origin)
        if (session.show_tos_modal) {
          // Terms not yet accepted: nothing renders until they are. Every
          // page a not-yet-accepted user may see (/accept-terms, the terms
          // and privacy texts, sign-in) short-circuits through
          // PRE_AUTH_ROUTES above, so no exemption list is needed here —
          // reaching this component at all means the path is gated.
          // Accepting POSTs to Flask, which stamps tos_accepted_at and
          // redirects to /matches; the fresh boot then loads clean.
          window.location.replace('/accept-terms')
          setState({ status: 'needs-tos' })
          return
        }
        setShownOrg(session.org?.id ?? null)
        setState({ status: 'ready', session })
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.isUnauthenticated) {
          // Straight to the login page (Ben, 2026-08-29). This branch is
          // reachable ONLY from the boot fetch above — nothing handles a 401
          // arriving later — so "anonymous" here means "not signed in when
          // the app loaded" and there is no half-finished work to explain
          // away. That was the objection to redirecting, and it does not
          // apply to a state we can only enter before anything has happened.
          //
          // replace(), not assign(): with assign the login page's Back button
          // returns here, which redirects again — a trap you cannot leave.
          window.location.replace(signInUrl())
          setState({ status: 'anonymous' })
          return
        }
        setState({ status: 'error', message: e instanceof Error ? e.message : String(e) })
      })
  }, [])

  // A page that moved the session to another club (a staff page opening
  // that club's match) leaves the shell naming the old one; reload the
  // session so the rail, and every list read under it, names the club the
  // server is really in.
  useEffect(() => {
    const onSwitch = () => {
      getSession()
        .then((session) => {
          setShownOrg(session.org?.id ?? null)
          setState((st) => (st.status === 'ready' ? { status: 'ready', session } : st))
        })
        .catch(() => window.location.reload())
    }
    window.addEventListener(ORG_SWITCHED_EVENT, onSwitch)
    return () => window.removeEventListener(ORG_SWITCHED_EVENT, onSwitch)
  }, [])

  if (state.status === 'loading') {
    return <div className="boot muted">Loading…</div>
  }

  if (state.status === 'anonymous') {
    // The redirect above is already running; this renders for the instant
    // before it lands, and permanently if the browser refused it (an
    // extension, or a navigation blocked mid-flight). A dead end with no way
    // out would be worse than the extra link.
    return (
      <div className="boot">
        <h1>Priori</h1>
        <p className="muted">Taking you to sign in…</p>
        <a className="boot__cta" href={signInUrl()}>Sign in</a>
      </div>
    )
  }

  if (state.status === 'needs-tos') {
    return (
      <div className="boot">
        <h1>Priori</h1>
        <p className="muted">One step left — accepting the Terms of Service.</p>
        <a className="boot__cta" href="/accept-terms">Review and accept</a>
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className="boot">
        <h1>Priori</h1>
        <p className="error">
          Could not reach the API {API_BASE ? `at ${API_BASE}` : 'on this origin'} — {state.message}
        </p>
        {/* This panel has now given wrong advice twice.

            It used to say "check CORS_ORIGINS", which was advice from before
            the proxy — the browser only ever talks to one origin now. It then
            said "start it with docker compose up -d db api", which on
            2026-08-29 was wrong for both outages of the same afternoon: once
            the Docker daemon itself was not running, and once the containers
            were healthy and the SSH tunnel to dev's database had died.

            The lesson is that this component cannot know which link broke —
            it only knows the request failed. So it no longer guesses. It
            points at the one thing that CAN tell the difference, which checks
            the whole chain in the order it breaks. */}
        <p className="muted">
          {import.meta.env.DEV
            ? <>Something in the local chain is down — the Docker daemon, the
                containers, the database tunnel, or the API itself. This page
                cannot tell which. Run <code>scripts/dev-doctor.sh</code>; it
                names the first thing that is false.</>
            : 'The frontend is serving, but its proxy could not reach the API.'}
        </p>
      </div>
    )
  }

  const { session } = state
  return (
    <BrowserRouter>
      <PageViewTracker />
      {/* Pages nested several layers deep read the session from context rather
          than a prop threaded through each of them. Same object App already
          fetched — no second request. */}
      <SessionProvider session={session}>
      <AppShell session={session}>
        <Routes>
          {/* The Match Room (Ben, 2026-10-23): the index at /match-room, the
              match page at /match-room/<code>. The rail, the addresses and
              the page titles all say the same thing. */}
          <Route path="/match-room" element={<Games session={session} />} />
          {/* The Clip Room's home (Ben, 2026-10-23): the club's reels —
              merged in from /playlists/ — above the season list whose rows
              open the Clip Lab. */}
          <Route path="/clip-room" element={<ClipRoom session={session} />} />
          {/* Renamed 2026-08-29 (/games -> /matches) and again 2026-10-23
              (/matches -> /match-room). Redirects, not deletions: these paths
              are in bookmarks, commit messages and the Status page's own
              navigation table. */}
          <Route path="/matches" element={<Navigate to="/match-room" replace />} />
          <Route path="/games" element={<Navigate to="/match-room" replace />} />
          <Route path="/manage-users" element={<ManageUsers session={session} tab="club" />} />
          <Route path="/manage-users/staff" element={<ManageUsers session={session} tab="staff" />} />
          <Route path="/manage-staff" element={<Navigate to="/manage-users/staff" replace />} />
          {/* Manage Games folded into Match Administration: its settings are
              the Matches tab's gear (Ben, 2026-10-16) */}
          <Route path="/manage-games" element={<Navigate to="/ops/layer-stack" replace />} />
          <Route path="/calibration" element={<CalibrationQueue session={session} />} />
          <Route path="/lead-links" element={<LeadLinks session={session} />} />
          <Route path="/manage-clubs" element={<ManageClubs session={session} />} />
          {/* The club's own credentials. Server-gated: a viewer sees only
              their own personal tokens, an admin sees the club's. */}
          {/* API Tokens became the "API keys" tab of Developer Tools
              (Ben, 2026-08-30). */}
          <Route path="/api-tokens"
                 element={<Navigate to="/developer-tools?tab=keys" replace />} />
          {/* Priori's cross-club view. staff read, platform admin revokes. */}
          {/* Manage API absorbed both the staff route inventory and the
              cross-club token screen (Ben, 2026-08-30). Both old paths
              redirect into the tab that replaced them. */}
          <Route path="/manage-api" element={<ManageApi session={session} />} />
          <Route path="/manage-api-tokens"
                 element={<Navigate to="/manage-api?tab=tokens" replace />} />
          {/* Renamed 2026-08-29. Redirects rather than deletions: these paths
              are in commit messages, the Status page's own navigation table
              and anything anyone bookmarked this week. */}
          <Route path="/users" element={<Navigate to="/manage-users" replace />} />
          <Route path="/manage-orgs" element={<Navigate to="/manage-clubs" replace />} />
          <Route path="/activity" element={<Activity />} />
          {/* Manage Insights — the management surface, at /manage-insights
              since 2026-09-07 to match its rail label; it held /insights-lab
              before the Insights Lab (the workshop below) took that address. */}
          <Route path="/manage-insights"
                 element={<ManageInsights session={session} tab="games" />} />
          <Route path="/manage-insights/games"
                 element={<ManageInsights session={session} tab="games" />} />
          <Route path="/manage-insights/registry"
                 element={<ManageInsights session={session} tab="registry" />} />
          <Route path="/manage-insights/builds"
                 element={<ManageInsights session={session} tab="builds" />} />
          <Route path="/manage-insights/game/:gameCode"
                 element={<InsightsGame session={session} />} />
          <Route path="/manage-insights/pack/:name/:version/:gameCode"
                 element={<InsightPackViewer session={session} />} />
          {/* The management page's old addresses. The two literal tabs and
              the pre-registry 'packs' name redirect by name; the game and
              report deep links keep their tails. /insights-lab itself is NOT
              here — it is the Insights Lab now, below. */}
          <Route path="/insights-lab/games"
                 element={<Navigate to="/manage-insights/games" replace />} />
          <Route path="/insights-lab/registry"
                 element={<Navigate to="/manage-insights/registry" replace />} />
          <Route path="/insights-lab/packs"
                 element={<Navigate to="/manage-insights/registry" replace />} />
          <Route path="/insights-lab/game/:gameCode"
                 element={<LabToManageRedirect />} />
          {/* The standalone Insights Packs page the management tabs absorbed
              (2026-09-01). */}
          <Route path="/insights-packs"
                 element={<Navigate to="/manage-insights/games" replace />} />
          {/* The Insights Lab — six tabs (Ben, 2026-09-02 as Analytics
              Collaboration; renamed to the rail's own name and moved to
              /insights-lab 2026-09-07, with Build a dashboard as a tab). */}
          {/* The Analysis Room (Ben, 2026-10-21; the Insights Workshop
              before): the club-facing tier (per-club flag `workshop`; the
              server decides access again). Shares no code with the Lab. */}
          <Route path="/analysis-room"
                 element={<InsightsWorkshop session={session} tab="collaborate" />} />
          <Route path="/analysis-room/dashboard"
                 element={<InsightsWorkshop session={session} tab="dashboard" />} />
          <Route path="/analysis-room/segment"
                 element={<InsightsWorkshop session={session} tab="segment" />} />
          <Route path="/analysis-room/dictionary"
                 element={<InsightsWorkshop session={session} tab="dictionary" />} />
          <Route path="/analysis-room/attacking-structure"
                 element={<InsightsWorkshop session={session} tab="attacking" />} />
          <Route path="/analysis-room/defensive-control"
                 element={<InsightsWorkshop session={session} tab="defensive" />} />
          <Route path="/analysis-room/provoke-pressing"
                 element={<InsightsWorkshop session={session} tab="provoke" />} />
          <Route path="/analysis-room/formation-dynamics"
                 element={<InsightsWorkshop session={session} tab="formations" />} />
          {/* Its old address, every tab: a redirect, not a deletion. */}
          <Route path="/insights-workshop/*" element={<LegacyWorkshopRedirect />} />
          <Route path="/insights-lab"
                 element={<AnalyticsCollaboration session={session} tab="collaborate" />} />
          <Route path="/insights-lab/dashboard"
                 element={<AnalyticsCollaboration session={session} tab="dashboard" />} />
          <Route path="/insights-lab/playground"
                 element={<AnalyticsCollaboration session={session} tab="playground" />} />
          <Route path="/insights-lab/pitch"
                 element={<AnalyticsCollaboration session={session} tab="pitch" />} />
          <Route path="/insights-lab/builder"
                 element={<AnalyticsCollaboration session={session} tab="builder" />} />
          <Route path="/insights-lab/segment"
                 element={<AnalyticsCollaboration session={session} tab="segment" />} />
          <Route path="/insights-lab/attacking-structure"
                 element={<AnalyticsCollaboration session={session} tab="attstructure" />} />
          <Route path="/insights-lab/defensive-control"
                 element={<AnalyticsCollaboration session={session} tab="defcontrol" />} />
          <Route path="/insights-lab/provoke-press"
                 element={<AnalyticsCollaboration session={session} tab="provoke" />} />
          <Route path="/insights-lab/provoke-press-v2"
                 element={<AnalyticsCollaboration session={session} tab="provoke2" />} />
          <Route path="/insights-lab/formation-dynamics"
                 element={<AnalyticsCollaboration session={session} tab="formations" />} />
          <Route path="/insights-lab/possession-flow"
                 element={<AnalyticsCollaboration session={session} tab="flow" />} />
          <Route path="/insights-lab/data-dictionary"
                 element={<AnalyticsCollaboration session={session} tab="dictionary" />} />
          {/* Supporting Calculations was removed (Ben, 2026-09-30): it ran
              legacy derived-table backfills. Its address lands on the Lab. */}
          <Route path="/insights-lab/calculations"
                 element={<Navigate to="/insights-lab" replace />} />
          {/* The lab's old address and the standalone pages before it. */}
          <Route path="/analytics-collaboration"
                 element={<Navigate to="/insights-lab" replace />} />
          <Route path="/analytics-collaboration/playground"
                 element={<Navigate to="/insights-lab/playground" replace />} />
          <Route path="/analytics-collaboration/pitch"
                 element={<Navigate to="/insights-lab/pitch" replace />} />
          <Route path="/analytics-collaboration/builder"
                 element={<Navigate to="/insights-lab/builder" replace />} />
          <Route path="/analytics-collaboration/segment"
                 element={<Navigate to="/insights-lab/segment" replace />} />
          <Route path="/insights-playground"
                 element={<Navigate to="/insights-lab/playground" replace />} />
          <Route path="/insights-pitch"
                 element={<Navigate to="/insights-lab/pitch" replace />} />
          <Route path="/visual-builder"
                 element={<Navigate to="/insights-lab/builder" replace />} />
          {/* The pre-registry report address, still in old bookmarks: the
              analytic viewer it pointed at is gone, so it lands on the games tab. */}
          <Route path="/insights-packs/:gameCode/:packId"
                 element={<Navigate to="/manage-insights/games" replace />} />
          {/* Data Management removed (Ben, 2026-10-16) */}
          <Route path="/pipeline/data-management" element={<Navigate to="/ops/layer-stack" replace />} />
          {/* QA Studio removed: nothing on it is the stack's (Ben, 2026-10-17) */}
          <Route path="/pipeline/qa-studio" element={<Navigate to="/ops/layer-stack" replace />} />
          <Route path="/ops/layer-stack" element={<LayerStackOps session={session} />} />
          {/* Opened in its own window from the Layers page. */}
          <Route path="/ops/video-times/:gameId" element={<VideoTimes />} />
          {/* One route with an optional param, not two siblings: crossing
              between sibling routes REMOUNTS the page, so the first
              selection (which writes the id into the URL) refetched the
              game list and dropped the club filter. */}
          <Route path="/ops/match-lab/:gameId?" element={<MatchLab />} />
          <Route path="/ops/tracking-qa" element={<VideoQA />} />
          <Route path="/ops/tracking-qa/:gameId" element={<VideoQA />} />
          {/* the name it shipped at for a day */}
          <Route path="/ops/video-qa" element={<VideoQA />} />
          <Route path="/ops/video-qa/:gameId" element={<VideoQA />} />
          <Route path="/video-studio-v2" element={<QaStudioV2 />} />
          {/* The address it shipped at for one afternoon. */}
          <Route path="/pipeline/qa-studio-v2"
                 element={<Navigate to="/video-studio-v2" replace />} />
          <Route path="/pipeline" element={<Operations session={session} />} />
          {/* The review screen, at the address it has always had. The Jinja
              fallback that lived at /calibration/orig was retired (Phase 1). */}
          <Route path="/games/:jobId/calibration" element={<CalibrationReview session={session} />} />
          {/* The QA labs, at the addresses they have always had — Phase 2
              of the port took /qa off the proxy list. The games index
              merged into QA Studio (one table, not two to drift);
              /qa/games bookmarks land there. */}
          <Route path="/qa/games"
                 element={session.user.qa_enabled
                   ? <Navigate to="/ops/layer-stack" replace /> : <NotFound />} />
          {/* qa_enabled is decided server-side (every staff account,
              admins automatically — Ben, 2026-09-23) and gates these
              shells the way Flask gated the Jinja pages; every
              /api/v1/qa/* handler enforces the same rule itself. */}
          <Route path="/qa/tracking-compare/:gameCode"
                 element={session.user.qa_enabled
                   ? <TrackingCompare /> : <NotFound />} />
          <Route path="/qa/formations/:gameCode"
                 element={session.user.qa_enabled
                   ? <FormationsLab /> : <NotFound />} />
          <Route path="/qa/regains/:gameCode"
                 element={session.user.qa_enabled
                   ? <RegainsLab /> : <NotFound />} />
          {/* The labeling suite, at the addresses it has always had —
              Phase 3 of the port. Auth parity with Flask: any signed-in
              user (the Jinja pages were ProtectedBlueprint with no role
              gate); the /labeling/* APIs stay on Flask behind the same
              proxy prefix. */}
          <Route path="/labeling" element={<LabelingIndex />} />
          <Route path="/labeling/ball-grid" element={<BallGrid />} />
          <Route path="/labeling/jersey-legibility" element={<JerseyLegibility />} />
          <Route path="/labeling/jersey-grid" element={<JerseyNumbers />} />
          <Route path="/labeling/sequence" element={<SequenceLabeler />} />
          <Route path="/games/:jobId/calibration/orig" element={<CalibOrigRedirect />} />
          {/* Phase-1 flips: the Jinja pages 302 on the API origin; these
              absorb the same bookmarks on the front door. */}
          <Route path="/ops/*" element={<OpsRedirect />} />
          <Route path="/fixtures/*" element={<FixturesRedirect />} />
          <Route path="/pipeline/operations"
                 element={<Navigate to="/pipeline" replace />} />
          {/* Trailing slash: the rail's href kept Flask's, and a link that
              renders nothing is the same bug as a missing route. */}
          {/* The match page, at the Match Room's own address. /match-video
              was the kept name until 2026-10-23; it redirects below, as
              /game-match-summary (the Flask name) and /tactical-lab already
              did — stored notification links and bookmarks carry all three. */}
          <Route
            path="/match-room/:gameCode"
            element={
              <Suspense fallback={<p className="muted">Loading the match…</p>}>
                <MatchVideoRoute />
              </Suspense>
            }
          />
          <Route path="/match-video/:gameCode" element={<LegacyMatchRedirect />} />
          {/* The Clip Room's match page (Ben, 2026-10-23): /clip-lab was its
              address while it was only a tab of the match page. */}
          <Route
            path="/clip-room/:gameCode"
            element={
              <Suspense fallback={<p className="muted">Loading…</p>}>
                <DataDrivenClipping />
              </Suspense>
            }
          />
          <Route path="/clip-lab/:gameCode" element={<LegacyClipRedirect />} />
          <Route path="/tactical-lab/:gameCode" element={<LegacyMatchRedirect />} />
          {/* Performance summaries — React since Phase 4 of the port, at
              their original addresses. Flask keeps the /api/v1 data
              endpoints and a 302 shell for direct API-origin hits. */}
          <Route path="/attacking-match-summary/:gameCode"
                 element={<PerformanceSummary pageType="attacking" />} />
          <Route path="/defending-match-summary/:gameCode"
                 element={<PerformanceSummary pageType="defending" />} />
          <Route path="/glossary/" element={<Glossary />} />
          {/* The Reels index lives in the Clip Room now (2026-10-23); reel
              deep links below keep their addresses. */}
          <Route path="/playlists/" element={<Navigate to="/clip-room" replace />} />
          <Route path="/playlists/:playlistId" element={<PlaylistEditor />} />
          <Route path="/playlists/:playlistId/play" element={<PlaylistPlayer />} />
          <Route path="/dashboard" element={<Dashboard session={session} />} />
          <Route path="/upload-match" element={<UploadMatch session={session} />} />
          <Route path="/home" element={<Navigate to="/dashboard" replace />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/reports/:runId" element={<ReportViewer />} />
          {/* The page was /feed until 2026-08-30 (Ben). Kept as a redirect
              rather than deleted: staff have it bookmarked, and the nav
              already listed /insights as an active prefix, so the rename was
              half-made already. */}
          <Route path="/feed" element={<Navigate to="/insights" replace />} />
          {/* The Notebooks SECTION is gone (Ben, 2026-08-30). Its one
              unique capability — the club's notebooks key — was always a
              SERVICE api_token minted through the same chokepoint, so it
              already lists and issues under Developer Tools. The DETAIL
              page is React since Phase 5 (Insights recap links deep-link
              it); Flask keeps everything one level deeper — the generated
              artifacts the runner writes. */}
          <Route path="/notebooks/:name" element={<NotebookDetail />} />
          <Route path="/glossary" element={<Navigate to="/glossary/" replace />} />
          <Route path="/developer-tools" element={<DeveloperTools />} />
          {/* Merged into Status as a tab (Ben, 2026-08-30). Redirected
              rather than removed: it was a rail entry for months and
              people have it bookmarked. */}
          <Route path="/database-info"
                 element={<Navigate to="/status?tab=database" replace />} />
          {/* Merged into Status. Kept as a redirect rather than deleted:
              links to it exist in commit messages, the build document and
              anything anyone bookmarked. */}
          <Route path="/resiliency" element={<Navigate to="/status" replace />} />
          <Route path="/status" element={<Status session={session} />} />
          {/* Where signing in lands. Staff get /status, everyone else
              gets /dashboard. Both are React routes. */}
          <Route path="/" element={<Landing session={session} />} />
          {/* An unknown path says so. It used to redirect to the landing page,
              which made every dead link look like a working one — five rail
              links were broken for hours behind that. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AppShell>
      </SessionProvider>
    </BrowserRouter>
  )
}
